import { graphqlRequest, postRequest } from './amplify/helpers';
import { customAccountTeamMembersByUserId } from '@/graphql/custom_queries';
import { appsByAppKey, userSettingsByOwner } from '@/graphql/queries';
import { IntegratedApp } from '@/constants/apps';

const normalizeTeamMembers = (teamMembers) => {
  return teamMembers.map((member) => {
    return {
      ...member,
      user: member.user.items[0],
      account: member.account.items[0],
    };
  });
};

export const fetchCurrentAccountDetails = async (userId, loginAppKey = '') => {
  try {
    const response = await graphqlRequest({
      query: customAccountTeamMembersByUserId,
      variables: {
        userId,
        sortDirection: 'DESC',
      },
    });

    const accounts = normalizeTeamMembers(response?.data?.accountTeamMembersByUserId?.items);

    if (!accounts?.length || !accounts[0].appKey) {
      throw new Error(`could not fetch ${userId} account`);
    }
    let currentAccount;

    if (loginAppKey) {
      currentAccount = accounts.find((account) => account.appKey === loginAppKey);
    }

    if (!currentAccount) {
      [currentAccount] = accounts;
    }

    const { appKey, role } = currentAccount;

    return { appKey, isAccountOwner: role === 'owner', owner: currentAccount.owner, accounts };
  } catch (error) {
    console.error('Error fetching current account details', error);
    throw error;
  }
};

export const fetchUserSettingsByOwner = async ({ owner }) => {
  const response = await graphqlRequest({
    query: userSettingsByOwner,
    variables: {
      owner,
    },
  });
  return response?.data?.userSettingsByOwner;
};

export const fetchFirstShopifyAppUrl = async (appKey) => {
  try {
    const response = await graphqlRequest({
      query: appsByAppKey,
      variables: {
        appKey,
      },
    });

    return response?.data?.appsByAppKey?.items?.find((app) => {
      return app.active && [IntegratedApp.SHOPIFY, IntegratedApp.STUDIO_SHOPIFY].includes(app.app);
    })?.appUrl;
  } catch (error) {
    console.error('Error fetching first Shopify app URL', error);
    throw error;
  }
};

class AccountServiceClass {
  private shopifyAuthCompletionPromise: Promise<any> | null = null;

  completeShopifyAuth = async ({ tempId, appKey }) => {
    if (this.shopifyAuthCompletionPromise) {
      return this.shopifyAuthCompletionPromise;
    }

    try {
      this.shopifyAuthCompletionPromise = postRequest({
        apiName: 'shopify',
        path: '/shopify/complete-shopify-auth',
        options: {
          body: {
            appKey: appKey,
            tempId,
            shopifyApp: 'shopifyStudio',
          },
        },
      });
      return this.shopifyAuthCompletionPromise;
    } catch (error) {
      console.error('Error completing Shopify auth', error);
      this.shopifyAuthCompletionPromise = null;
      throw error;
    }
  };
}

export const AccountService = new AccountServiceClass();
